import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const ContactPage = () => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title and meta tags
    document.title = 'İletişim - Es Donanım Bilgisayar | Ankara Ulus';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Es Donanım Bilgisayar ile iletişime geçin. Adres: Anafartalar Cad. Ulus Şehir Çarşısı No:3/11 Altındağ/Ankara. Telefon: 0506 537 69 85. 2. el bilgisayar ve teknik servis için bizi arayın.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'iletişim, es donanım bilgisayar iletişim, ankara bilgisayar servisi iletişim, ulus bilgisayar, bilgisayar tamiri iletişim');
    }
    
    // Set canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/iletisim`);
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Es Donanım Bilgisayar",
        "image": `${siteUrl}/logo.png`,
        "telephone": "+905065376985",
        "email": "info@esbilgisayar.com.tr",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Anafartalar Cad. Ulus Şehir Çarşısı No:3/11",
          "addressLocality": "Altındağ",
          "addressRegion": "Ankara",
          "postalCode": "06030",
          "addressCountry": "TR"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "39.9450",
          "longitude": "32.8597"
        },
        "url": siteUrl,
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "19:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "09:00",
            "closes": "18:00"
          }
        ]
      }
    });
    document.head.appendChild(schemaScript);
    
    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6o1mj130mftb86r3cmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pb-16 lg:pb-0">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary via-primary-light to-primary">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 font-heading">
              İletişim
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              2. El Bilgisayar Alım Satım, Bilgisayar Tamiri ve Teknik Servis için Bize Ulaşın
            </p>
          </div>
        </section>

        {/* Contact Cards */}
        <section className="py-16 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a
                href="tel:+905065376985"
                className="flex items-center space-x-4 p-8 bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl hover:shadow-card-hover transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-secondary/20 rounded-xl group-hover:bg-secondary transition-colors">
                  <i className="ri-phone-line text-3xl text-secondary group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <p className="text-sm text-neutral-600 mb-1">Telefon</p>
                  <p className="font-bold text-primary text-lg">0506 537 69 85</p>
                </div>
              </a>

              <a
                href="https://wa.me/905065376985"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-4 p-8 bg-gradient-to-br from-whatsapp/10 to-whatsapp/5 rounded-xl hover:shadow-card-hover transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-whatsapp/20 rounded-xl group-hover:bg-whatsapp transition-colors">
                  <i className="ri-whatsapp-line text-3xl text-whatsapp group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <p className="text-sm text-neutral-600 mb-1">WhatsApp</p>
                  <p className="font-bold text-primary text-lg">Mesaj Gönder</p>
                </div>
              </a>

              <a
                href="mailto:info@esbilgisayar.com.tr"
                className="flex items-center space-x-4 p-8 bg-gradient-to-br from-accent/10 to-accent/5 rounded-xl hover:shadow-card-hover transition-all cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-accent/20 rounded-xl group-hover:bg-accent transition-colors">
                  <i className="ri-mail-line text-3xl text-accent group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <p className="text-sm text-neutral-600 mb-1">E-posta</p>
                  <p className="font-bold text-primary text-lg">info@esbilgisayar.com.tr</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-24 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-card">
                  <h2 className="text-3xl font-bold text-primary mb-6">Bize Ulaşın</h2>
                  <p className="text-neutral-600 mb-8">
                    2. el bilgisayar, laptop tamiri ve teknik servis hizmetlerimiz hakkında bilgi almak için formu doldurun
                  </p>

                  <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-neutral-700 mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:border-secondary outline-none transition-colors"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-neutral-700 mb-2">
                          E-posta *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:border-secondary outline-none transition-colors"
                          placeholder="ornek@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-neutral-700 mb-2">
                          Telefon *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:border-secondary outline-none transition-colors"
                          placeholder="05XX XXX XX XX"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-neutral-700 mb-2">
                        Konu *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:border-secondary outline-none transition-colors cursor-pointer"
                      >
                        <option value="">Konu seçin</option>
                        <option value="2. El Bilgisayar Alım">2. El Bilgisayar Alım</option>
                        <option value="2. El Bilgisayar Satış">2. El Bilgisayar Satış</option>
                        <option value="Bilgisayar Tamiri">Bilgisayar Tamiri</option>
                        <option value="Laptop Tamiri">Laptop Tamiri</option>
                        <option value="Teknik Servis">Teknik Servis</option>
                        <option value="Kurumsal Destek">Kurumsal Destek</option>
                        <option value="Diğer">Diğer</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-neutral-700 mb-2">
                        Mesajınız *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={6}
                        className="w-full px-4 py-3 border-2 border-neutral-200 rounded-lg focus:border-secondary outline-none transition-colors resize-none"
                        placeholder="Mesajınızı buraya yazın... (Maksimum 500 karakter)"
                      ></textarea>
                      <p className="text-sm text-neutral-500 mt-2">{formData.message.length}/500 karakter</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-secondary hover:bg-secondary-dark text-white rounded-lg font-semibold text-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700 font-semibold">Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.</p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 font-semibold">Bir hata oluştu. Lütfen tekrar deneyin veya telefon ile iletişime geçin.</p>
                      </div>
                    )}
                  </form>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-2 space-y-8">
                {/* Map */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-card">
                  <div className="h-80">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057.5!2d32.8597!3d39.9408!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d34f9b0e0e0e0f%3A0x0!2sAnafartalar+Cd.%2C+Ulus%2C+Alt%C4%B1nda%C4%9F%2FAnkara!5e0!3m2!1str!2str!4v1700000000000"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Es Donanım Bilgisayar Konum"
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start space-x-3">
                      <i className="ri-map-pin-line text-secondary text-xl mt-1"></i>
                      <div>
                        <p className="font-semibold text-primary mb-1">Adres</p>
                        <p className="text-neutral-600 text-sm">
                          Anafartalar Cad. Ulus Şehir Çarşısı No:3/11<br />
                          Altındağ / ANKARA
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="bg-white rounded-2xl p-8 shadow-card">
                  <h3 className="text-xl font-bold text-primary mb-6 flex items-center space-x-2">
                    <i className="ri-time-line text-secondary"></i>
                    <span>Çalışma Saatleri</span>
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                      <span className="text-neutral-600">Pazartesi - Cuma</span>
                      <span className="font-semibold text-primary">09:00 - 19:00</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                      <span className="text-neutral-600">Cumartesi</span>
                      <span className="font-semibold text-primary">09:00 - 17:00</span>
                    </div>
                    <div className="flex items-center justify-between py-3">
                      <span className="text-neutral-600">Pazar</span>
                      <span className="font-semibold text-secondary">Kapalı</span>
                    </div>
                  </div>
                </div>

                {/* Quick Contact */}
                <div className="bg-gradient-to-br from-primary via-primary-light to-primary rounded-2xl p-8 text-white">
                  <h3 className="text-xl font-bold mb-4">Acil Durum?</h3>
                  <p className="text-white/90 mb-6">
                    Bilgisayar tamiri ve teknik servis için hemen arayın
                  </p>
                  <a
                    href="tel:+905065376985"
                    className="flex items-center justify-center space-x-2 w-full px-6 py-3 bg-secondary hover:bg-secondary-dark text-white rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    <span>0506 537 69 85</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ContactPage;